/**
 * Welcher Funnel gehört zu dieser Adresse?
 *
 * Steht getrennt von main.tsx, weil main.tsx beim Laden das Dokument anfasst
 * und sich deshalb nicht ohne Browser prüfen lässt. Diese Entscheidung ist
 * reine Rechnerei und soll auch so prüfbar sein.
 */

/** Der Pfad, unter dem die Funnel liegen. */
export const PFADPRAEFIX = "/anfrage/";

/**
 * Liest den Kurznamen aus einem Pfad wie `/anfrage/gartenarbeit/`.
 *
 * Gibt `null` zurück, wenn der Pfad keine Funnel-Adresse ist. Erlaubt sind nur
 * die Zeichen, die ein Kurzname haben darf, und nur ein einziger Abschnitt.
 * Der Wert wandert in eine Anfrage an die API; er wird dort zwar kodiert, aber
 * was hier gar nicht erst durchkommt, muss dort niemand abfangen.
 */
export function slugAusPfad(pfad: string): string | null {
  if (!pfad.startsWith(PFADPRAEFIX)) return null;

  const rest = pfad.slice(PFADPRAEFIX.length).replace(/\/+$/, "");
  if (!/^[a-z0-9-]+$/.test(rest)) return null;

  // Die Danke-Seite eines Bereichs gehört zu demselben Funnel: Sie zeigt
  // dessen Abschlusstexte und braucht deshalb dessen Schema.
  return rest.endsWith(DANKE_ENDUNG) ? rest.slice(0, -DANKE_ENDUNG.length) : rest;
}

/** Woran eine Danke-Seite in der Adresse zu erkennen ist. */
const DANKE_ENDUNG = "-danke";

/**
 * Ist diese Adresse die Danke-Seite eines Funnels?
 *
 * Auf fremden Seiten sagt das Attribut `data-danke` am Behälter dasselbe. Der
 * Pfad ist der Weg für die eigenen Seiten - und der einzige, der in der
 * Entwicklung greift, wo für jede Adresse dieselbe index.html ausgeliefert
 * wird.
 */
export function istDankeadresse(pfad: string): boolean {
  if (!pfad.startsWith(PFADPRAEFIX)) return false;
  const rest = pfad.slice(PFADPRAEFIX.length).replace(/\/+$/, "");
  return /^[a-z0-9-]+$/.test(rest) && rest.endsWith(DANKE_ENDUNG);
}

/**
 * Liest eine Postleitzahl aus der Adresse.
 *
 * Die Startseite fragt Gewerk und Postleitzahl gemeinsam ab und hängt die
 * Postleitzahl an die Adresse der Anfrage-Seite (`?plz=66111`). Der Funnel
 * trägt sie dann in sein eigenes Feld ein, statt sie ein zweites Mal zu
 * verlangen - wer eine Angabe zweimal machen soll, denkt an ein kaputtes
 * Formular.
 *
 * Nur fünf Ziffern, sonst nichts. Der Wert kommt aus der Adresszeile und ist
 * damit von außen bestimmt; er landet in einer Antwort, die später an die API
 * geht.
 */
export function plzAusAdresse(suchteil: string): string | null {
  const wert = new URLSearchParams(suchteil).get("plz");
  return wert !== null && /^[0-9]{5}$/.test(wert) ? wert : null;
}
